import React, { useState, useEffect } from 'react';
import {
  Box, Typography, TextField, Button, Grid, Paper, IconButton, MenuItem, Stack, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Checkbox,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import { getSigningCredentials, getClaimTemplate, getAppConfig } from '../../services/api';

const CLAIM_TYPES = ['text', 'number', 'date', 'boolean', 'url'];

const blankClaim = (required = false) => ({ key: '', label: '', type: 'text', required });

// Author a verifiable credential as a flat list of claims (the fields signed
// into every credential of this type). Used for both create and edit.
const CredentialTypeForm = ({ initial, onSave, onCancel }) => {
  const [name, setName] = useState(initial?.name || '');
  const [description, setDescription] = useState(initial?.description || '');
  const [claims, setClaims] = useState(
    initial?.claims && initial.claims.length ? initial.claims.map((c) => ({ ...blankClaim(), ...c })) : [blankClaim(true)]
  );
  const [signingCredentialId, setSigningCredentialId] = useState(initial?.signingCredentialId || '');
  const [signers, setSigners] = useState([]);
  const [error, setError] = useState(null);
  const [showTrustGraph, setShowTrustGraph] = useState(false);

  useEffect(() => { getSigningCredentials().then((s) => setSigners(Array.isArray(s) ? s : [])).catch(() => setSigners([])); }, []);
  useEffect(() => { getAppConfig().then((c) => setShowTrustGraph(!!c?.showTrustGraphMetadata)).catch(() => {}); }, []);

  const updateClaim = (i, field, value) =>
    setClaims((prev) => prev.map((c, idx) => (idx === i ? { ...c, [field]: value } : c)));
  const addClaim = () => setClaims((prev) => [...prev, blankClaim()]);
  const removeClaim = (i) => setClaims((prev) => prev.filter((_, idx) => idx !== i));

  // Pull the claim set straight from the trust graph's verified transparency
  // indicators, so the credential signs exactly what the graph already certifies.
  const prepopulateFromGraph = async () => {
    try {
      const { claims: template } = await getClaimTemplate();
      if (!Array.isArray(template) || !template.length) { setError('No trust-graph claims available.'); return; }
      setError(null);
      setClaims((prev) => {
        const existing = new Set(prev.map((c) => (c.key || '').trim()).filter(Boolean));
        const mapped = template
          .map((t) => ({ key: t.key, label: t.label, type: t.type || 'text', required: !!t.required }))
          .filter((t) => !existing.has(t.key));
        const kept = prev.filter((c) => (c.key || '').trim());
        return [...kept, ...mapped].length ? [...kept, ...mapped] : [blankClaim(true)];
      });
    } catch (e) {
      setError('Could not load trust-graph claims.');
    }
  };

  const handleSave = () => {
    if (!name.trim()) { setError('Credential name is required.'); return; }
    const cleaned = claims
      .map((c) => ({ key: (c.key || '').trim(), label: (c.label || '').trim(), type: c.type || 'text', required: !!c.required }))
      .filter((c) => c.key);
    if (cleaned.length === 0) { setError('Add at least one claim.'); return; }
    onSave({
      ...(initial || {}),
      name: name.trim(),
      description: description.trim(),
      type_name: initial?.type_name || `${name.trim().replace(/[^a-zA-Z0-9]/g, '')}Credential`,
      claims: cleaned,
      signingCredentialId: signingCredentialId || null,
    });
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>{initial ? 'Edit' : 'New'} verifiable credential</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Define the credential and the list of claims you'll sign into it for your members.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth label="Credential name" placeholder="e.g. JTI Certified Journalist"
            value={name} onChange={(e) => setName(e.target.value)} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth label="Description" placeholder="What this credential attests"
            value={description} onChange={(e) => setDescription(e.target.value)} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField select fullWidth label="Default signing key" value={signingCredentialId}
            onChange={(e) => setSigningCredentialId(e.target.value)}
            helperText={signers.length ? 'Signs credentials of this type (overridable at issue)' : 'Uses the vc-service default signer'}>
            <MenuItem value=""><em>Default signer</em></MenuItem>
            {signers.map((c) => (
              <MenuItem key={c.id} value={c.id}>{c.name || c.id}{(c.isDefault || c.is_default) ? ' (default)' : ''}</MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Claims</Typography>
        {showTrustGraph && (
          <Button size="small" startIcon={<AccountTreeIcon />} onClick={prepopulateFromGraph}
            sx={{ color: '#76B900' }}>
            Prepopulate from Trust Graph
          </Button>
        )}
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Each claim is a field signed into every credential of this type{showTrustGraph
          ? ' — or pull the verified transparency indicators straight from the Trust Graph.'
          : '.'}
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '30%' }}>Claim field</TableCell>
              <TableCell sx={{ width: '30%' }}>Label</TableCell>
              <TableCell sx={{ width: '20%' }}>Type</TableCell>
              <TableCell align="center">Required</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {claims.map((c, i) => (
              <TableRow key={i}>
                <TableCell>
                  <TextField size="small" fullWidth placeholder="outletName" value={c.key}
                    onChange={(e) => updateClaim(i, 'key', e.target.value)} />
                </TableCell>
                <TableCell>
                  <TextField size="small" fullWidth placeholder="Outlet Name" value={c.label}
                    onChange={(e) => updateClaim(i, 'label', e.target.value)} />
                </TableCell>
                <TableCell>
                  <TextField select size="small" fullWidth value={c.type || 'text'}
                    onChange={(e) => updateClaim(i, 'type', e.target.value)}>
                    {CLAIM_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                  </TextField>
                </TableCell>
                <TableCell align="center">
                  <Checkbox checked={!!c.required} onChange={(e) => updateClaim(i, 'required', e.target.checked)} />
                </TableCell>
                <TableCell>
                  <IconButton size="small" color="error" onClick={() => removeClaim(i)} disabled={claims.length === 1}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Button startIcon={<AddIcon />} onClick={addClaim} sx={{ mt: 1 }}>Add claim</Button>

      <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
        <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave}>Save credential</Button>
        <Button onClick={onCancel}>Cancel</Button>
      </Stack>
    </Box>
  );
};

export default CredentialTypeForm;
